import {
  type Attributes,
  context,
  logs,
  ROOT_CONTEXT,
  SeverityNumber,
  trace,
} from "@opentelemetry/api";
import { StackContextManager } from "@opentelemetry/sdk-trace";
import { browserTelemetry } from "@pax/api-contract/telemetry";
import {
  clientAction,
  clientAttributes,
  configureClientTelemetry,
  reportClientError,
} from "@pax/telemetry/client";
import { startClientSdk } from "@pax/telemetry/client-sdk";

declare const __PAX_BUILD_VERSION__: string;
const SESSION_ID_STORAGE_KEY = "pax.telemetry.session_id";
let userId: string | null = null;

export function tabSessionId(): string {
  const existing = sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
  if (existing) return existing;
  const created = crypto.randomUUID();
  sessionStorage.setItem(SESSION_ID_STORAGE_KEY, created);
  return created;
}

export function setTelemetryUser(id: string | null): void {
  userId = id;
}

export function startBrowserTelemetry(): void {
  context.setGlobalContextManager(new StackContextManager().enable());
  configureClientTelemetry({
    origin: location.origin,
    attributes: () => ({
      "session.id": tabSessionId(),
      "url.path": location.pathname,
      ...(userId ? { "user.id": userId } : {}),
    }),
  });
  const sdk = startClientSdk({
    service: "web",
    version: __PAX_BUILD_VERSION__,
    sessionId: tabSessionId(),
    endpoint: signal => new URL(browserTelemetry.path(signal), location.origin).href,
  });
  forwardConsole("warn", SeverityNumber.WARN);
  forwardConsole("error", SeverityNumber.ERROR);
  window.addEventListener("error", event =>
    reportClientError(event.error ?? new Error(event.message)),
  );
  window.addEventListener("unhandledrejection", event => reportClientError(event.reason));
  void import("./session-recording").then(({ startSessionRecording }) => {
    startSessionRecording({
      resource: sdk.resource,
      url: new URL(browserTelemetry.path("logs"), location.origin).href,
    });
    markSession("record init");
  });
  document.addEventListener("visibilitychange", () => {
    markSession("visibility", { hidden: document.hidden });
    if (document.hidden) void sdk.flush();
  });
  window.addEventListener("pagehide", () => {
    void sdk.flush();
  });
  window.addEventListener("focus", () => markSession("focus", { focused: true }));
  window.addEventListener("blur", () => markSession("focus", { focused: false }));
}

function markSession(name: string, attributes: Attributes = {}): void {
  trace
    .getTracer("web")
    .startSpan(name, { attributes: { ...clientAttributes(), ...attributes } }, ROOT_CONTEXT)
    .end();
}

function forwardConsole(level: "warn" | "error", severityNumber: SeverityNumber): void {
  const original = console[level];
  console[level] = (...args: unknown[]) => {
    original(...args);
    for (const error of args.filter(value => value instanceof Error)) reportClientError(error);
    const words = args.filter(value => !(value instanceof Error));
    if (!words.length) return;
    logs.getLogger("web.console").emit({
      severityNumber,
      severityText: level.toUpperCase(),
      context: context.active(),
      body: words.map(value => String(value)).join(" "),
      attributes: clientAttributes(),
    });
  };
}

export function traceAction<Result>(
  name: string,
  run: (traceparent: string) => Result,
  attributes: Attributes = {},
): Result {
  return clientAction(name, operation => run(operation.traceparent), attributes);
}
